import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from 'antd';
import {
  LIMIT_METRIC_CHOICES,
  LIMIT_METRIC_DURATION,
  LIMIT_METRIC_PORTFOLIO,
  MetricsLimitsNew,
  selectors,
  updateMetricsLimits,
} from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import { metricInputSettings } from '../../SessionSettings/MetricsLimits2';
import InlineNumber from './InlineNumber';
import InlineCheck from './InlineCheck';
import './inline_edit.css';

type State = { [metricType: number]: MetricsLimitsNew };

/**
 * Seamless v2 of the Metric Limits & Visibility table. Same wiring as
 * MetricsLimits2 (reads session.metrics_limits, dispatches updateMetricsLimits
 * with the full set), but with NO edit mode: every cell is an always-mounted
 * control. Numeric bounds commit on blur/Enter; the Enforced/Visible checkboxes
 * apply instantly. Because nothing swaps in/out, the card never resizes.
 */
const MetricsCardV2: React.FC = () => {
  const dispatch = useDispatch();
  const { metrics_limits } = useSelector(selectors.activeSession);
  const buildLocal = (): State => {
    const next: State = {};
    metrics_limits.forEach(a => {
      next[a.metric_type] = {
        id: a.id,
        metric_type: a.metric_type,
        lower_limit: a.lower_limit,
        upper_limit: a.upper_limit,
        enforced: a.enforced,
        visible: a.visible,
      };
    });
    return next;
  };
  // Initialise from the session so the first paint shows real values (no flash).
  const [local, setLocal] = useState<State>(buildLocal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setLocal(buildLocal()), [metrics_limits]);

  // Preserve the original row-inclusion quirk: index the array by metric_type.
  const rows = Object.entries(LIMIT_METRIC_CHOICES)
    .filter(([k]) => metrics_limits[Number(k)] !== undefined)
    .map(([k, name]) => ({ metricType: Number(k), name: String(name) }));

  const push = (next: State) => {
    setLocal(next);
    dispatch(updateMetricsLimits({ limits: Object.values(next) }));
  };

  const setField = (
    metricType: number,
    field: keyof MetricsLimitsNew,
    value: number | boolean,
  ) => {
    const cur = local[metricType];
    if (!cur) return;
    push({
      ...local,
      [metricType]: { ...cur, metric_type: metricType, [field]: value },
    });
  };

  if (rows.length === 0) {
    return (
      <SettingsCard title="Metric Limits & Visibility">
        <div className="set-studio__placeholder" style={{ minHeight: 80 }}>
          <Typography.Text type="secondary">
            Select and save some instruments.
          </Typography.Text>
        </div>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard title="Metric Limits & Visibility">
      <table className="ie-table">
        <colgroup>
          <col style={{ width: '34%' }} />
          <col style={{ width: '24%' }} />
          <col style={{ width: '24%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Metric</th>
            <th className="ie-col-num">Lower</th>
            <th className="ie-col-num">Upper</th>
            <th className="ie-col-center">Enforced</th>
            <th className="ie-col-center">Visible</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const obj = local[r.metricType];
            const s = metricInputSettings[r.metricType] || {};
            const enforcedDisabled =
              r.metricType === LIMIT_METRIC_DURATION ||
              r.metricType === LIMIT_METRIC_PORTFOLIO;
            return (
              <tr key={r.metricType}>
                <td className="ie-row-label">{r.name}</td>
                <td className="ie-col-num">
                  <InlineNumber
                    value={obj?.lower_limit ?? null}
                    onCommit={v => setField(r.metricType, 'lower_limit', v)}
                    formatter={s.formatter}
                    parser={s.parser}
                    min={s.min}
                    max={s.max}
                    step={s.step}
                    ariaLabel={`${r.name} lower limit`}
                  />
                </td>
                <td className="ie-col-num">
                  <InlineNumber
                    value={obj?.upper_limit ?? null}
                    onCommit={v => setField(r.metricType, 'upper_limit', v)}
                    formatter={s.formatter}
                    parser={s.parser}
                    min={s.min}
                    max={s.max}
                    step={s.step}
                    ariaLabel={`${r.name} upper limit`}
                  />
                </td>
                <td className="ie-col-center">
                  <InlineCheck
                    checked={!!obj?.enforced}
                    disabled={enforcedDisabled}
                    onToggle={c => setField(r.metricType, 'enforced', c)}
                    ariaLabel={`${r.name} enforced`}
                  />
                </td>
                <td className="ie-col-center">
                  <InlineCheck
                    checked={!!obj?.visible}
                    onToggle={c => setField(r.metricType, 'visible', c)}
                    ariaLabel={`${r.name} visible`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SettingsCard>
  );
};

export default MetricsCardV2;
